import React, { useEffect, useRef } from "react";
import { FormikContextType, useFormikContext } from "formik";
import * as _ from "lodash";
import AppFormLabel from "../../views/components/forms/AppFormLabel";

interface SecurityCodeInputProps {
  name: string;
  label?: string;
}

function SecurityCodeInput({ name, label }: SecurityCodeInputProps) {
  const { setFieldValue, values, touched, errors }: FormikContextType<any> =
    useFormikContext();
  const firstInputRef = useRef<HTMLInputElement | null>(null);

  const securityCode: string = values[name] || "";

  useEffect(() => {
    if (firstInputRef.current) {
      firstInputRef.current.focus();
    }
  }, []);

  const handleKeyDown =
    (index: number) => (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Backspace" && !securityCode[index]) {
        // If backspace is pressed and current field is empty, focus previous field
        const prevField: HTMLInputElement | null = document.getElementById(
          `security-code-${index - 1}`
        ) as HTMLInputElement;
        if (prevField) {
          prevField.focus();
        }
      }
    };

  const handleChange =
    (index: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const digit = event.target.value.replace(/[^0-9]/g, "").slice(0, 1);

      // Construct the new code by replacing the digit at the correct index
      const newCode = `${securityCode.substring(
        0,
        index
      )}${digit}${securityCode.substring(index + 1)}`;
      setFieldValue(name, newCode);

      // Automatically focus next field if there's a next field and current input is filled
      if (digit && index < 5) {
        const nextField: HTMLInputElement | null = document.getElementById(
          `security-code-${index + 1}`
        ) as HTMLInputElement;
        if (nextField) {
          nextField.focus();
        }
      }
    };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    const pasteData = event.clipboardData
      .getData("text")
      .replace(/[^0-9]/g, "")
      .slice(0, 6);
    const newCode = pasteData.padEnd(6, " "); // Ensure the string has length 6
    setFieldValue(name, newCode);

    // Set focus to the correct field after pasting
    const nextIndex = Math.min(pasteData.length, 5);
    const nextField: HTMLInputElement | null = document.getElementById(
      `security-code-${nextIndex}`
    ) as HTMLInputElement;
    if (nextField) {
      nextField.focus();
    }

    event.preventDefault(); // Prevent the default paste action
  };

  return (
    <div className="mb-4">
      {label && (
        <div className="mb-2">
          <AppFormLabel label={label} required />
        </div>
      )}
      <div className="d-flex justify-content-center mb-2">
        {Array.from({ length: 6 }).map((_, index) => (
          <input
            key={index}
            id={`security-code-${index}`}
            name={`${name}-${index}`}
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={1}
            className="form-control text-center mx-1"
            style={{ width: "3em", fontSize: "1.5rem", padding: "0.5em" }}
            onPaste={handlePaste}
            onKeyDown={handleKeyDown(index)}
            onChange={handleChange(index)}
            value={securityCode[index] || ""}
            ref={index === 0 ? firstInputRef : null}
          />
        ))}
      </div>
      {_.get(touched, name) && _.get(errors, name) && (
        <div className="d-flex justify-content-center mt-2">
          <small className="text-danger" style={{ fontSize: 14 }}>
            {_.get(errors, name) as any}
          </small>
        </div>
      )}
    </div>
  );
}

export default SecurityCodeInput;
